import React, { useMemo } from 'react';
import * as yup from 'yup';
import { DModal, RhDateInput, RhFormWrapper, RHTextField, RhSingleAutocomplete } from '../../../../dum';
import {
    useGetPayrollPeriodsQuery,
    useGetPayrollEntriesQuery,
    useUpsertPayrollPeriodMutation,
    useUpsertPayrollEntryMutation,
    useOrderProductionBranchInputQuery,
    useOrderProductionEmployeeInputQuery,
    useEmployeesPageQuery,
} from '../../../../../services';
import { getDateFromString, getStringFromDate } from '../../../../../helpers';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    periodId: number | null;
    onSuccess: () => void;
}

interface IForm {
    start_date: string | null;
    end_date: string | null;
    week_number: number;
    branch_id: number | null;
}

const schema: yup.SchemaOf<IForm> = yup.object({
    start_date: yup.string().required(),
    end_date: yup.string().required(),
    week_number: yup.number().required().min(1),
    branch_id: yup.number().required().min(1),
});

const defaultEntry = {
    estatus: 'ALTA',
    control_bono_area: false,
    control_deposito: false,
    sueldo: 0,
    jo: 48,
    ht: 0,
    he: 0,
    he_override: false,
    retardos: 0,
    faltas: 0,
    vac: 0,
    dias_festivos: 0,
    infonavit: 0,
    fonacot: 0,
    descuento_prestamos: 0,
    otros_menos: 0,
    entregas_especiales: 0,
    viajes: 0,
    bonos: 0,
    otros_mas: 0,
    bd: 0,
    observaciones: '',
};

// ---------------------------------------------------------------------------
// Branch preview — reads branch_id directly from form context so it never
// triggers a parent re-render (which would cause RhFormWrapper to reset).
// ---------------------------------------------------------------------------
interface IBranchPreviewProps {
    employees: { id: number; fullname: string; is_inactive?: boolean | null; branch_id?: number | null; order_production_type_id?: number | null }[];
    orderProductionTypes: { id: number; name: string }[];
}

function BranchPreview({ employees, orderProductionTypes }: IBranchPreviewProps) {
    const { watch } = useFormContext();
    const branchId: number | null = watch('branch_id');

    const previewEmployees = branchId
        ? employees.filter((e) => !e.is_inactive && e.branch_id === branchId)
        : [];

    if (!branchId || previewEmployees.length === 0) return null;

    const grouped = orderProductionTypes
        .map((type) => ({
            type,
            employees: previewEmployees.filter((e) => e.order_production_type_id === type.id),
        }))
        .filter((g) => g.employees.length > 0);

    const untyped = previewEmployees.filter((e) => !e.order_production_type_id);

    return (
        <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2">Empleados a incluir</Typography>
                <Chip label={previewEmployees.length} size="small" color="primary" />
            </Box>
            {grouped.map(({ type, employees: grpEmps }) => (
                <Box key={type.id} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        {type.name} ({grpEmps.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {grpEmps.map((e) => (
                            <Chip key={e.id} label={e.fullname} size="small" variant="outlined" />
                        ))}
                    </Box>
                </Box>
            ))}
            {untyped.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Sin tipo ({untyped.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {untyped.map((e) => (
                            <Chip key={e.id} label={e.fullname} size="small" variant="outlined" />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Edit-mode summary — shows headcount + totals from existing entries.
// ---------------------------------------------------------------------------
function EditSummary({ periodId }: { periodId: number }) {
    const { data } = useGetPayrollEntriesQuery({
        variables: { PayrollPeriodId: periodId },
    });

    const entries = data?.getPayrollEntries ?? [];
    if (entries.length === 0) return null;

    const totalPlus = entries.reduce((sum, e) => sum + e.total_plus, 0);
    const totalMinus = entries.reduce((sum, e) => sum + e.total_minus, 0);
    const grandTotal = entries.reduce((sum, e) => sum + e.total, 0);

    const fmt = (n: number) =>
        n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

    return (
        <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2">Resumen</Typography>
                <Chip label={`${entries.length} personas`} size="small" color="primary" />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {entries.map((e) => (
                    <Chip key={e.id} label={e.employee_name || `#${e.employee_id}`} size="small" variant="outlined" />
                ))}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Total percepciones</Typography>
                    <Typography variant="body2">{fmt(totalPlus)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Total deducciones</Typography>
                    <Typography variant="body2">{fmt(totalMinus)}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2">Total neto</Typography>
                    <Typography variant="subtitle2" color="primary">{fmt(grandTotal)}</Typography>
                </Box>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------
export function SUpsertPayrollPeriodDialog({ open, setOpen, periodId, onSuccess }: IProps) {
    const { data: periodsData } = useGetPayrollPeriodsQuery({
        variables: { branch_id: null },
        skip: !open,
    });

    const { data: branchData } = useOrderProductionBranchInputQuery({ skip: !open });

    const { data: employeeData } = useOrderProductionEmployeeInputQuery({
        variables: { excludeDiscontinued: true },
        skip: !open || !!periodId,
    });

    const { data: typesData } = useEmployeesPageQuery({
        variables: { take: 0, skip: 0, filter: '' },
        skip: !open || !!periodId,
    });

    const [upsertPeriod] = useUpsertPayrollPeriodMutation();
    const [upsertEntry] = useUpsertPayrollEntryMutation();

    const period = periodId
        ? periodsData?.getPayrollPeriods.find((p) => p.id === periodId)
        : undefined;

    // Stable reference so RhFormWrapper's reset useEffect doesn't fire on
    // every render. Only recalculates when the period record itself changes.
    const initialValues: IForm = useMemo(
        () => ({
            start_date: period ? getDateFromString(period.start_date) : null,
            end_date: period ? getDateFromString(period.end_date) : null,
            week_number: period?.week_number ?? 1,
            branch_id: period?.branch_id ?? null,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [period?.id, period?.start_date, period?.end_date, period?.week_number, period?.branch_id],
    );

    const handleSubmit = async (values: IForm) => {
        const result = await upsertPeriod({
            variables: {
                PayrollPeriodInput: {
                    id: periodId,
                    start_date: values.start_date ? getStringFromDate(values.start_date) : null,
                    end_date: values.end_date ? getStringFromDate(values.end_date) : null,
                    week_number: values.week_number,
                    branch_id: values.branch_id,
                },
            },
        });

        if (!periodId && result.data?.upsertPayrollPeriod) {
            const newPeriodId = result.data.upsertPayrollPeriod.id;
            const activeEmployees = (employeeData?.getEmployees ?? []).filter(
                (e) => !e.is_inactive && e.branch_id === values.branch_id,
            );
            await Promise.all(
                activeEmployees.map((emp) =>
                    upsertEntry({
                        variables: {
                            PayrollEntryInput: {
                                payroll_period_id: newPeriodId,
                                employee_id: emp.id,
                                ...defaultEntry,
                                // Snapshot the employee's base pay + schedule so
                                // 48 vs 52.5 flows into the entry (jo drives the
                                // HE/pay formulas); both stay editable per week.
                                sueldo: emp.base_salary,
                                jo: emp.hours_should_work || defaultEntry.jo,
                            },
                        },
                    }),
                ),
            );
        }

        onSuccess();
        setOpen(false);
    };

    const employees = employeeData?.getEmployees ?? [];
    const orderProductionTypes = typesData?.getOrderProductionTypes ?? [];

    return (
        <DModal title="Período de nómina" open={open} setOpen={setOpen}>
            <RhFormWrapper
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                <RHTextField name="week_number" label="Semana #" />
                <RhSingleAutocomplete
                    name="branch_id"
                    label="Sucursal"
                    options={branchData?.getBranches ?? []}
                    itemValue="id"
                    itemText="name"
                />
                <RhDateInput name="start_date" label="Fecha inicio" />
                <RhDateInput name="end_date" label="Fecha fin" />

                {!periodId && (
                    <BranchPreview
                        employees={employees}
                        orderProductionTypes={orderProductionTypes}
                    />
                )}
            </RhFormWrapper>

            {periodId && <EditSummary periodId={periodId} />}
        </DModal>
    );
}
